/**
 * @author Fernando Ruano
 * @file saga.js
 * @description Glucometry Saga
 */

// Dependencies
import { put, call, takeLatest } from 'redux-saga/effects';

// Global actions
import { setResponse, setLoading } from '@nephro/redux/stores/app/actions';

// Services
import glucometryService from '@nephro/services/glucometry-service';

// Constants
import {
  CREATE_GLUCOMETRY,
  DELETE_GLUCOMETRY_BY_ID,
  GET_ALL_GLUCOMETRIES,
  GET_GLUCOMETRY_BY_ID,
  UPDATE_GLUCOMETRY,
  GET_GLUCOMETRY_BY_PATIENT_ID,
  GET_ALL_GLUCOMETRY_TIMES
} from './constants';

const customError = {
  type: 'Error',
  message: 'commons.problem'
};

/**
 * Call service to get glucometries from backend
 * @param { object } action - Action with payload data
 */
export function* getGlucometriesArray(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { paged, page, limit } = data;

  try {
    const response = yield call(
      glucometryService.getGlucometries,
      paged,
      limit,
      page
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get glucometry times from backend
 * @param { object } action - Action with payload data
 */
export function* getGlucometryTimesArray(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const { callback } = action;

  try {
    const response = yield call(glucometryService.getGlucometryTimes);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get glucometry info by id
 * @param { object } action - Action with payload data
 */
export function* getGlucometryById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(glucometryService.getGlucometryById, id);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.glucometryNotFound' || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
}

/**
 * Call service to get glucometry info by patient id
 * @param { object } action - Action with payload data
 */
export function* getGlucometryByPatientId(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(glucometryService.getGlucometryByPatientId, id);
    yield put(setResponse('success', false, false, { ...response }, callback));
    yield put(setLoading(false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.glucometryNotFound' || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
}

/**
 * Call service to create glucometry
 * @param { object } action - Action with payload data
 */
export function* createGlucometry(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;

  try {
    const response = yield call(glucometryService.createGlucometry, data);
    yield put(
      setResponse(
        'success',
        'new glucometry',
        'patientRecords.recordCreated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to update glucometry
 * @param { object } action - Action with payload data
 */
export function* updateGlucometry(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(glucometryService.updateGlucometry, data, id);
    yield put(
      setResponse(
        'success',
        'updated glucometry',
        'patientRecords.recordUpdated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to delete glucometry by id
 * @param { object } action - Action with payload data
 */
export function* deleteGlucometryById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(glucometryService.deleteGlucometryById, id);
    yield put(
      setResponse(
        'success',
        'deleted glucometry',
        'patientRecords.recordDeleted',
        { ...response },
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  // yield put(setLoading(false));
}

/**
 * Declare all component sagas
 */
export function* watchGlucometrySaga() {
  yield takeLatest(GET_ALL_GLUCOMETRIES, getGlucometriesArray);
  yield takeLatest(GET_ALL_GLUCOMETRY_TIMES, getGlucometryTimesArray);
  yield takeLatest(GET_GLUCOMETRY_BY_ID, getGlucometryById);
  yield takeLatest(GET_GLUCOMETRY_BY_PATIENT_ID, getGlucometryByPatientId);
  yield takeLatest(CREATE_GLUCOMETRY, createGlucometry);
  yield takeLatest(UPDATE_GLUCOMETRY, updateGlucometry);
  yield takeLatest(DELETE_GLUCOMETRY_BY_ID, deleteGlucometryById);
}
